import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HTTPCONSTANTS, MerchantService } from '@odin/odin-core';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import {
  type CsvReportLink,
  type CsvReportRequest,
  type IFormattedTransaction,
  type IOrderTracking,
  type ITransaction,
  type ITransactionDetail,
  TransactionFiltersParams,
} from '../models';
import { DashboardMetrics } from "../../../../../../apps/odin-ui/src/app/dashboard-metrics/dashboard-metrics.component";
import type { WebhooksMessageAttempt } from '../models/WebhooksMessageAttempt';
import { DevModeChooserService } from 'libs/odin-core/src/lib/services/dev-mode-chooser.service';

@Injectable()
export class TransactionsService {
  private readonly baseUrl: string = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}`;
  /** Portal BFF, deployed separately from Odin so the detail page can be rolled out in stages */
  private readonly portalUrl: string = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.baseAddress}/portal`;

  constructor(
    private readonly http: HttpClient,
    private merchantService: MerchantService,
    private devModeChooserService: DevModeChooserService
  ) { }

   
  getMetrics(interval: number): Observable<DashboardMetrics> {
    const merchantRef = this.merchantService.getCurrentBuid();
    if (merchantRef === null || merchantRef === undefined) return of();
    const merchantMid = this.merchantService.getCurrentMid();
    if (merchantMid === null || merchantMid === undefined) return of();

    if (merchantMid === null || merchantMid === undefined || merchantMid.monekId === null || merchantMid.monekId === undefined || merchantMid.monekId === '') {
      return this.http.get<DashboardMetrics>(
        `${this.baseUrl}/transactions/${merchantRef.id
        }/metrics/interval/${interval.toString()}`,
      );
    } else {
      return this.http.get<DashboardMetrics>(
        `${this.baseUrl}/transactions/${merchantRef.id
        }/${merchantMid.monekId}/metrics/interval/${interval.toString()}`,
      );
    }
  }

  getTransaction(crossRef: string, monekId?: string): Observable<ITransaction> {
    let merchantRef = this.merchantService.getCurrentBuid();
    const merchantMid = monekId ?? this.merchantService.getCurrentMid()?.monekId;

    // If monekId is provided, try to get the BUID for it
    if (monekId) {
      const buid = this.merchantService.getBuidByMonekId(monekId);
      if (buid) {
        merchantRef = buid;
      }
    }

    if (merchantRef === null) return of();
    if (!merchantMid) return of();

    let params = new HttpParams().set('monekid', merchantMid);

    if (this.devModeChooserService.prodModeEnabled) {
      params = params.set('prod', '1');
    }

    return this.http.get<ITransaction>(
      `${this.baseUrl}/transactions/${merchantRef.id}/transaction/${crossRef}`,
      { params },
    );
  }

  /**
   * Single call powering the Transaction Detail page: transaction, related
   * transactions, TX360 events, order tracking, card info and technical data.
   * Served by the Portal BFF, not Odin.
   * Callback attempts are NOT included — they load separately (3rd party).
   */
  getTransactionDetail(crossRef: string, monekId?: string): Observable<ITransactionDetail> {
    const merchantRef = this.resolveBuid(monekId);
    if (merchantRef === null) return of();

    return this.http.get<ITransactionDetail>(
      `${this.portalUrl}/transactions/${merchantRef}/transaction/${crossRef}/detail`,
    );
  }

  /** Sets the merchant-defined order status for a transaction. Returns the updated tracking state. */
  updateOrderStatus(crossRef: string, status: string, monekId?: string): Observable<IOrderTracking> {
    const merchantRef = this.resolveBuid(monekId);
    if (merchantRef === null) return of();

    return this.http.put<IOrderTracking>(
      `${this.portalUrl}/transactions/${merchantRef}/transaction/${crossRef}/order-status`,
      { status },
    );
  }

  /** BUID for the given monekId where known, otherwise the currently selected BUID. */
  private resolveBuid(monekId?: string): string | null {
    if (monekId) {
      const buid = this.merchantService.getBuidByMonekId(monekId);
      if (buid) return buid.id;
    }
    return this.merchantService.getCurrentBuid()?.id ?? null;
  }

  getTransactions(
    params: TransactionFiltersParams,
    buid: string,
    mid: string
  ): Observable<ITransaction[]> {
    const url = this.constructUrl(buid, mid, params.query !== '');
    const httpParams = this.buildQueryParams(params);

    return this.http.get<ITransaction[]>(url, { params: httpParams });
  }

  findTransaction(merchantRef: string, mid: string, id: string): Observable<ITransaction> {
    const url = this.constructUrlWithId(merchantRef, mid, id);

    return this.http.get<ITransaction>(url);
  }

  downloadCsvReport(
    params: CsvReportRequest,
    merchantRef: string,
  ): Observable<CsvReportLink> {
    const merchantMid = this.merchantService.getCurrentMid();
    if (merchantMid === null || merchantMid === undefined) return of();

    let httpParams = new HttpParams().set('monekId', merchantMid.monekId);

    if (this.devModeChooserService.prodModeEnabled) {
      httpParams = httpParams.set('prod', '1');
    }

    const url = `${this.baseUrl}/transactions/csv/${merchantRef}`;

    return this.http.post<CsvReportLink>(url, params, { params: httpParams });
  }

  // Resend transaction callback with Svix
  resendTransactionCallback(crossRef: string): Observable<void> {
    const merchantRef = this.merchantService.getCurrentBuid();
    const monekId = this.merchantService.getCurrentMid()?.monekId;
    if (merchantRef === null) return of();
    const params = new HttpParams().set('monekId', monekId ?? '');
    const url = `${this.baseUrl}/transactions/webhooks/${merchantRef.id}/resend/${crossRef}`;
    return this.http.post<void>(url, {}, { params });
  }

  // Get transaction callback details with Svix
  getTransactionCallback(
    crossRef: string
  ): Observable<WebhooksMessageAttempt> {
    const merchantRef = this.merchantService.getCurrentBuid();
    const monekId = this.merchantService.getCurrentMid()?.monekId;
    if (merchantRef === null) return of();
    const params = new HttpParams().set('monekId', monekId ?? '');
    const url = `${this.baseUrl}/transactions/webhooks/${merchantRef.id}/callbacks/${crossRef}`;
    return this.http.get<WebhooksMessageAttempt>(url, { params });
  }

  public GetRelatedTransactions(
    transaction: IFormattedTransaction,
    forceRelation?: 'parent' | 'children'
  ): Observable<ITransaction[]> {

    // transaction.merchant.monekId will be used for related transactions if provided,
    const relatedMonekId = transaction.merchant?.monekId && transaction.merchant.monekId !== ''
      ? transaction.merchant.monekId
      : this.merchantService.getCurrentMid()?.monekId;

    // If transaction.merchant.monekId is provided, get the BUID for it
    let buidRef = this.merchantService.getMerchantRef();
    if (transaction.merchant?.monekId && transaction.merchant.monekId !== '') {
      const buid = this.merchantService.getBuidByMonekId(transaction.merchant.monekId);
      if (buid) {
        buidRef = buid.id;
      }
    }

    if (!relatedMonekId) return of([]);

    const relationType =
      forceRelation !== undefined
        ? forceRelation
        : (transaction.transactionType === 'refund' || transaction.transactionType === 'reversal'
          ? 'parent'
          : 'children');

    let params = new HttpParams().set('monekId', relatedMonekId);

    if (this.devModeChooserService.prodModeEnabled) {
      params = params.set('prod', '1');
    }

    return this.http.get<ITransaction[]>(
      `${this.baseUrl}/transactions/${buidRef}/relation/${relationType}/${transaction.crossReference}`,
      { params },
    );
  }

  private buildQueryParams(params: TransactionFiltersParams): HttpParams {
    let httpParams = new HttpParams()
      .set('page', params.page.toString());

    if (params.take !== undefined)
      httpParams = httpParams.set('take', params.take.toString());

    if (params.query && params.query !== '')
      httpParams = httpParams.set('searchExpression', params.query);
    if (params.filters.minDate)
      httpParams = httpParams.set('minDate', moment(params.filters.minDate).format('MM/DD/YYYY'));
    if (params.filters.maxDate)
      httpParams = httpParams.set('maxDate', moment(params.filters.maxDate).add(1, 'days').format('MM/DD/YYYY'));
    if (params.filters.minAmount)
      httpParams = httpParams.set('minAmount', params.filters.minAmount.toString());
    if (params.filters.maxAmount)
      httpParams = httpParams.set('maxAmount', params.filters.maxAmount.toString());
    if (params.filters.channel)
      httpParams = httpParams.set('channel', this.formatEnumString(params.filters.channel));
    if (params.filters.status)
      httpParams = httpParams.set('status', this.formatEnumString(params.filters.status));

    if (this.devModeChooserService.prodModeEnabled) {
      httpParams = httpParams.set('prod', '1');
    }

    return httpParams;
  }

  private constructUrlWithId(
    buid: string,
    mid: string,
    transactionId: string,
  ): string {
    const url = this.constructUrl(buid, mid);

    return `${url}/details/${transactionId}`;
  }

  private constructUrl(buid: string, mid: string, hasQuery: boolean = false): string {
    if (mid === null || mid === undefined || mid === '') {
      if (hasQuery) return `${this.baseUrl}/transactions/${buid}/search`;
      return `${this.baseUrl}/transactions/${buid}`;
    } else {
      if (hasQuery) return `${this.baseUrl}/transactions/${buid}/${mid}/search`;
      return `${this.baseUrl}/transactions/${buid}/${mid}`;
    }
  }

  private formatEnumString(value: string): string {
    return value.replace(/ /g, '_').toLowerCase();
  }
}
